import React, {useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Row} from "reactstrap";
import Button from "../../../../../../components/base/button/button";
import {SvgIcon, Tooltip} from "@mui/material";
import SearchInput from "../../../../../../components/base/input/search_input";
import {Add, EditOutlined} from "@mui/icons-material";
import AddOrEditModal from "./modal/add_edit";
import Api from "../../../../../../../core/services/api/api";
import {GiftCardCategoriesDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";

const dataGridSaveConfig = {
    name: 'GiftCardCategoriesViewDataGrid',
    version: 1,
}

const GiftCardCategories = () => {
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [_categories, _setCategories] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [category, setCategory] = useState(null);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('gift-card-categories-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.giftCardsCategories);
        configureBehavioralTool((tags) => tags.giftCardsCategories);
    }, [configureBehavioralTool, configureNotebookTags]);


    /**
     * Loads the list of gift card categories as soon as the component mounts.
     */
    useEffect(() => {
        getGiftCardCategories().then();
    }, []);

    /**
     * Fetches the list of gift categories from the api and if the result is successful, sets the list.
     */
    const getGiftCardCategories = async () => {
        setLoading(true);
        const response = await Api.getGiftCardCategories();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            _setCategories(response?.data?.map(e => ({...e, isLoading: false})));
            setCategories(response?.data?.map(e => ({...e, isLoading: false})));
        }
        setLoading(false);
    }

    /**
     * Searches through the gift card categories to find the matches with the provided keywords
     * @param keywords {string};
     */
    const searchByKeyword = (keywords) => {
        if (!keywords?.trim()?.length) {
            setCategories(_categories);
        } else {
            const lowerKeywords = keywords.toLowerCase();
            setCategories(_categories.filter(e =>
                e.name?.toLowerCase()?.includes(lowerKeywords)
                || e.description?.toLowerCase().includes(lowerKeywords)
            ))
        }
    }

    /**
     * Shows the modal for editing the specified price category and sets the selected category to the selected one.
     * @param category {any}
     */
    const onEditGiftCardCategory = (category) => {
        setShowModal(true);
        setCategory(category);
    }

    /**
     * Shows the modal for adding a new category and sets the selected category to null.
     */
    const onAddGiftCardCategory = () => {
        setShowModal(true);
        setCategory(null);
    }

    /**
     * Edits the list of categories depending on whether a new category was added or a designated one was edited.
     * @param values {any} the new or edited category.
     */
    const onModalSuccess = (values) => {
        if (category) {
            // editing an existing price category
            _setCategories(prevState => prevState.map(e => e.id === category.id ? values : e));
            setCategories(prevState => prevState.map(e => e.id === category.id ? values : e));
        } else {
            // adding a new price category
            _setCategories(prevState => [values, ...prevState]);
            setCategories(prevState => [values, ...prevState]);
        }
    }

    /**
     * Removes a gift card category by calling the server and if the result of the server is successful, updates the
     * list in state.
     * @param category {any}
     */
    const removeGiftCardCategory = async (category) => {
        setCategories(prevState => prevState.map(e => e.id === category.id ? ({...e, isLoading: true}) : e))
        const response = await Api.removeGiftCardCategory(category.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setCategories(prevState => prevState?.filter(e => e.id !== category.id));
        }
        setCategories(prevState => prevState.map(e => e.id === category.id ? ({...e, isLoading: false}) : e))
    }

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: GiftCardCategoriesDataGridCellKeys.edit,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                resizable: false,
                width: 0,
            },
            {
                name: GiftCardCategoriesDataGridCellKeys.remove,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                resizable: false,
                width: 0,
            },
            {
                title: 'Title',
                name: GiftCardCategoriesDataGridCellKeys.title,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 200,
            },
            {
                title: "Description",
                name: GiftCardCategoriesDataGridCellKeys.description,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 300,
            },

        ], [])

    const [dataGridColumns, _, __, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Memo version of table row records used in data grid
     */
    const rows = useMemo(() => {
        return categories?.map(category => ({
            key: category.id,
            data: category,
            cells: {
                [GiftCardCategoriesDataGridCellKeys.edit]: <Tooltip title={'Edit'}>
                    <IconButton
                        color={'secondary'}
                        onPressed={() => onEditGiftCardCategory(category)}>
                        <EditOutlined className='icon normal'/>
                    </IconButton>
                </Tooltip>,
                [GiftCardCategoriesDataGridCellKeys.remove]: <Tooltip title={'Remove'}>
                    <IconButton
                        disabled={category.isLoading}
                        onPressed={() => removeGiftCardCategory(category)}
                        color={'paper.error'}
                        className={'remove-icon'}
                    >
                        <SvgIcon component={RemoveIcon} className='icon normal'/>
                    </IconButton>
                </Tooltip>,
                [GiftCardCategoriesDataGridCellKeys.title]: category.name,
                [GiftCardCategoriesDataGridCellKeys.description]: category.description,
            }
        })) ?? []
    }, [categories])

    return (
        <>
            <Row className='no-gutters w-100 justify-content-between align-items-center my-4'>
                <h2 className={'text-xl font-weight-semi-bold'}>
                    Gift Card Categories
                </h2>
                <Button
                    className='justify-content-center align-items-center d-flex'
                    variant={'outlined'}
                    color={'primary'}
                    onClick={onAddGiftCardCategory}
                >
                    <Add className='icon small'/>
                    <p className='ml-2'>Create New</p>
                </Button>
            </Row>
            <SearchInput
                loading={loading}
                autoSearch={true}
                search={searchByKeyword}
                label={'Search'}
            />
            <div className={'mb-5'}/>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={rows}
                hideFooter
                visibleRows={rows.length}
                hideExporting
                state={{
                    loading: {state: loading},
                }}
            />
            <AddOrEditModal
                open={showModal}
                setOpen={setShowModal}
                onSuccess={onModalSuccess}
                category={category}
            />
        </>
    );
}

export default GiftCardCategories;
